import { Injectable } from '@angular/core';
import type {
  Project,
  ProjectViewProject,
  ProjectViewUser,
  ThreadProject,
  UsersSelf,
} from '@freelancer/datastore/collections';
import { EnterpriseApi } from 'api-typings/common/common';
import {
  ProjectCollaborationStatusApi,
  type ProjectTypeApi,
} from 'api-typings/projects/projects';

interface EnrollmentOptions {
  language: 'englishOnly' | 'anyLanguage';
}

type ClientEnrollmentByProjectOptions = EnrollmentOptions & {
  /** An enterprise of `none` implies by default that the user is from the main FLN platform */
  enterprise:
    | 'any'
    | ('none' | EnterpriseApi.DELOITTE_DC | EnterpriseApi.FREIGHTLANCER)[];
  projectType: 'any' | ProjectTypeApi;
  hiremeType: 'hireMe' | 'nonHireMe' | 'any';
  localType: 'local' | 'nonLocal' | 'any';
  /**
   * Used to guard against race conditions in complex enrollment criteria
   * described in https://phabricator.tools.flnltd.com/w/gaf/app/abtest/#complex-enrolment-criteria
   *
   * NOT recommended to be false because this could lead to race conditions.
   */
  clientShouldBeProjectOwner: boolean;
} & (
    | {
        hasProjectCutoff: true;
        projectIdCutoff: number;
      }
    | {
        hasProjectCutoff: false;
      }
  );

type FreelancerEnrollmentByProjectOptions = EnrollmentOptions & {
  /** An enterprise of `none` implies by default that the user is from the main FLN platform */
  enterprise:
    | 'any'
    | ('none' | EnterpriseApi.DELOITTE_DC | EnterpriseApi.FREIGHTLANCER)[];
  projectType: 'any' | ProjectTypeApi;
  hiremeType: 'hireMe' | 'nonHireMe' | 'any';
  localType: 'local' | 'nonLocal' | 'any';
} & (
    | {
        hasProjectCutoff: true;
        projectIdCutoff: number;
      }
    | {
        hasProjectCutoff: false;
      }
  );

@Injectable({
  providedIn: 'root',
})
export class ABTestEnrollmentHelper {
  /**
   * This is a helper function to check if a freelancer (not a client) is
   * eligible to be enrolled in an experiment in the context of a project they are interacting with.
   */
  shouldEnrollFreelancerByProject(
    freelancer: ProjectViewUser | UsersSelf,
    project: Project | ProjectViewProject | ThreadProject,
    options: FreelancerEnrollmentByProjectOptions,
  ): boolean {
    if (
      options.language === 'englishOnly' &&
      freelancer.primaryLanguage !== 'en'
    ) {
      return false;
    }

    if (options.enterprise.includes('none')) {
      if (
        options.enterprise.length === 1 &&
        ((freelancer.enterpriseInternalNames &&
          freelancer.enterpriseInternalNames.length > 0) ||
          (project.enterpriseIds && project.enterpriseIds.length > 0))
      ) {
        return false;
      }
    } else {
      if (
        options.enterprise.includes(EnterpriseApi.DELOITTE_DC) &&
        !freelancer.enterpriseInternalNames?.includes(EnterpriseApi.DELOITTE_DC)
      ) {
        return false;
      }

      if (
        options.enterprise.includes(EnterpriseApi.FREIGHTLANCER) &&
        !freelancer.enterpriseInternalNames?.includes(
          EnterpriseApi.FREIGHTLANCER,
        )
      ) {
        return false;
      }
    }

    if (options.projectType !== 'any' && project.type !== options.projectType) {
      return false;
    }

    if (
      (options.hiremeType === 'hireMe' && !project.hireme) ||
      (options.hiremeType === 'nonHireMe' && project.hireme)
    ) {
      return false;
    }

    if (
      (options.localType === 'local' && !project.local) ||
      (options.localType === 'nonLocal' && project.local)
    ) {
      return false;
    }

    if (options.hasProjectCutoff && project.id <= options.projectIdCutoff) {
      return false;
    }

    const isCollaborator = project.projectCollaborations.some(
      collab =>
        collab.status === ProjectCollaborationStatusApi.ACTIVE &&
        collab.userId === freelancer.id,
    );

    if (freelancer.id === project.ownerId || isCollaborator) {
      return false;
    }

    return true;
  }

  /**
   * This is a helper function to check if a client (not a freelancer) is
   * eligible to be enrolled in an experiment based on the project they created.
   * A lot of experiments enroll users based on the project they created, and
   * usually those are clients that we wish to analyze, hence the need to
   * differentiate this function from the freelancer one.
   */
  shouldEnrollClientByProject(
    client: ProjectViewUser | UsersSelf,
    project: Project | ProjectViewProject | ThreadProject,
    options: ClientEnrollmentByProjectOptions,
  ): boolean {
    if (options.language === 'englishOnly' && client.primaryLanguage !== 'en') {
      return false;
    }

    if (options.enterprise.includes('none')) {
      if (
        options.enterprise.length === 1 &&
        ((client.enterpriseInternalNames &&
          client.enterpriseInternalNames.length > 0) ||
          (project.enterpriseIds && project.enterpriseIds.length > 0))
      ) {
        return false;
      }
    } else {
      if (
        options.enterprise.includes(EnterpriseApi.DELOITTE_DC) &&
        !client.enterpriseInternalNames?.includes(EnterpriseApi.DELOITTE_DC)
      ) {
        return false;
      }

      if (
        options.enterprise.includes(EnterpriseApi.FREIGHTLANCER) &&
        !client.enterpriseInternalNames?.includes(EnterpriseApi.FREIGHTLANCER)
      ) {
        return false;
      }
    }

    if (options.projectType !== 'any' && project.type !== options.projectType) {
      return false;
    }

    if (
      (options.hiremeType === 'hireMe' && !project.hireme) ||
      (options.hiremeType === 'nonHireMe' && project.hireme)
    ) {
      return false;
    }

    if (
      (options.localType === 'local' && !project.local) ||
      (options.localType === 'nonLocal' && project.local)
    ) {
      return false;
    }

    if (options.hasProjectCutoff && project.id <= options.projectIdCutoff) {
      return false;
    }

    if (options.clientShouldBeProjectOwner && client.id !== project.ownerId) {
      return false;
    }

    return true;
  }
}
